import { QUALITY_MAP } from '../utils/formatters.js';

const SETS = ['set1', 'set2'];
const GEAR_SLOTS = ['helmet', 'chest', 'pants', 'boots', 'weapon', 'ring', 'trinket1', 'trinket2', 'chanfron', 'saddle', 'peytral'];
const LEVEL_MAP = ['35', '40', '45', '50'];
const TOGGLES = [
    'toggle_attack', 'toggle_defense', 'toggle_dragon', 'toggle_march_size',
    'toggle_sop', 'toggle_creatures', 'toggle_gear_section', 'toggle_trinket_section', 'toggle_pit_section'
];

function getItemIdByName(slot, name) {
    if (!name) return 0;
    const dataSlot = slot.includes('trinket') ? 'trinket' : slot;
    const items = window.GEAR_DATA[dataSlot];
    if (!items) return 0;
    const item = items.find(item => item.name === name);
    return item ? item.id : 0;
}

function getItemNameById(slot, id) {
    if (!id) return '';
    const dataSlot = slot.includes('trinket') ? 'trinket' : slot;
    if (!window.GEAR_DATA) return '';
    const items = window.GEAR_DATA[dataSlot];
    if (!items) return '';
    // Backward compatibility: If the id parameter is small (e.g. < 50), it might be a legacy array index.
    // However, since we cleared the DB and our IDs start at 1000, we simply search by ID.
    // If it's a legacy index, it won't be found, which is an acceptable compromise for the new system.
    const item = items.find(item => item.id === id);
    return item ? item.name : '';
}

/**
 * Generates an ultra-compact "Share Link" representing the current gear and toggle selection.
 * To keep the URL incredibly short (~400 chars) and securely avoid DB storage:
 * - Specific string states (Names, Qualities, Levels) are mathematically mapped to integer indices.
 * - Empty fields are recorded structurally as 0.
 * - Boolean toggle states are compressed down to a single base-10 integer bitmask.
 * - Bulk Selector default states are intentionally ignored to prevent them from clobbering specific pieces upon restoral.
 * This flat numeric sequence is compressed via LZString Base64 and attached to the clipboard.
 */
export async function generateShareLink() {
    const form = document.getElementById('gearForm');

    // 1. Serialize Gear (Strict Integer Array)
    const gearVals = [];
    for (const setName of SETS) {
        for (const slot of GEAR_SLOTS) {
            // Name mapping
            const nameEl = form.elements[`${setName}_${slot}_name`];
            gearVals.push(getItemIdByName(slot, nameEl ? nameEl.value : ''));

            // Quality mapping
            const qualEl = form.elements[`${setName}_${slot}_quality`];
            const qIdx = qualEl ? QUALITY_MAP.indexOf(qualEl.value) : -1;
            gearVals.push(qIdx >= 0 ? qIdx + 1 : 0);

            // Level mapping
            const lvlEl = form.elements[`${setName}_${slot}_level`];
            const lIdx = lvlEl ? LEVEL_MAP.indexOf(lvlEl.value) : -1;
            gearVals.push(lIdx >= 0 ? lIdx + 1 : 0);
        }
    }

    // 2. Serialize Toggles as a binary integer
    let toggleInt = 0;
    for (let i = 0; i < TOGGLES.length; i++) {
        const el = document.getElementById(TOGGLES[i]);
        if (el && el.checked) {
            toggleInt |= (1 << i);
        }
    }

    const comparisonTitle = document.getElementById('comparisonTitle')?.value || window._savedComparisonTitle || '';
    // Format: flat sequence of digits separated by "."  (e.g "14.6.2.0.0.0..._152")
    let finalRawString = gearVals.join('.') + '_' + toggleInt;
    if (comparisonTitle.trim()) {
        finalRawString += '_' + encodeURIComponent(comparisonTitle.trim());
    }

    try {
        // Compress using LZString
        const compressedStr = LZString.compressToEncodedURIComponent(finalRawString);

        const url = new URL(window.location.href);
        url.search = ''; // Clear any existing parameters
        url.searchParams.set('c', compressedStr); // 'c' for compact new format

        // Append custom March Size parameters
        const msEl = document.getElementById('unequipped_march_size');
        const mwEl = document.getElementById('percent_toggle_waif');
        const mkeEl = document.getElementById('percent_toggle_keep_enh');
        const mdEl = document.getElementById('march_include_dragon');
        const mscKeep = document.getElementById('march_scenario_keep');
        const mscPlayerSop = document.getElementById('march_scenario_player_sop');
        const mscReinSop = document.getElementById('march_scenario_rein_sop');
        const mscCreatures = document.getElementById('march_scenario_creatures');

        if (msEl && msEl.value) url.searchParams.set('ms', msEl.value);
        if (mwEl && mwEl.checked) url.searchParams.set('mw', '1');
        if (mkeEl && mkeEl.checked) url.searchParams.set('mke', '1');
        if (mdEl && mdEl.checked) url.searchParams.set('md', '1');

        let mscVal = 'player_sop';
        if (mscKeep && mscKeep.checked) mscVal = 'keep';
        else if (mscReinSop && mscReinSop.checked) mscVal = 'rein_sop';
        else if (mscCreatures && mscCreatures.checked) mscVal = 'creatures';
        url.searchParams.set('msc', mscVal);

        const shareUrl = url.toString();

        await navigator.clipboard.writeText(shareUrl);
        alert('Share link copied to clipboard!');
    } catch (e) {
        console.error('Failed to generate share link:', e);
        alert('Failed to generate share link. Check console for details.');
    }
}

export function loadStateFromUrl() {
    const url = new URL(window.location.href);

    // 1. Target the new compact positional format
    const compactState = url.searchParams.get('c');
    let loaded = false;
    if (compactState) {
        loaded = loadCompactState(compactState);
    } else {
        // 2. Fallback to old format if a user opens a link generated yesterday
        const encodedState = url.searchParams.get('s');
        if (encodedState) {
            loaded = loadLegacyState(encodedState);
        }
    }

    if (loaded) {
        // Restore custom March Size inputs from URL params
        const ms = url.searchParams.get('ms');
        const mw = url.searchParams.get('mw');
        const mke = url.searchParams.get('mke');
        const mp = url.searchParams.get('mp');
        const md = url.searchParams.get('md');
        const msc = url.searchParams.get('msc') || 'player_sop';

        if (ms) {
            const msEl = document.getElementById('unequipped_march_size');
            if (msEl) {
                msEl.value = ms;
                // Dispatch input and change events so evaluation and compact linkage sync
                msEl.dispatchEvent(new Event('input', { bubbles: true }));
                msEl.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
        
        // Restore new percent toggles
        const mwEl = document.getElementById('percent_toggle_waif');
        const mkeEl = document.getElementById('percent_toggle_keep_enh');
        
        if (mwEl) {
            mwEl.checked = (mw === '1');
            mwEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (mkeEl) {
            mkeEl.checked = (mke === '1');
            mkeEl.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Backwards compatibility fallback for legacy 'mp' parameter
        if (mp) {
            const mpVal = parseFloat(mp);
            if (mpVal === 6.56) {
                if (mwEl) {
                    mwEl.checked = true;
                    mwEl.dispatchEvent(new Event('change', { bubbles: true }));
                }
            } else if (mpVal === 8 || mpVal === 8.0) {
                if (mkeEl) {
                    mkeEl.checked = true;
                    mkeEl.dispatchEvent(new Event('change', { bubbles: true }));
                }
            } else if (mpVal === 14.56) {
                if (mwEl) {
                    mwEl.checked = true;
                    mwEl.dispatchEvent(new Event('change', { bubbles: true }));
                }
                if (mkeEl) {
                    mkeEl.checked = true;
                    mkeEl.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        }
        if (md) {
            const mdEl = document.getElementById('march_include_dragon');
            if (mdEl) {
                mdEl.checked = md === '1';
                mdEl.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
        // Restore target scenario checkboxes
        const keepEl = document.getElementById('march_scenario_keep');
        const playerSopEl = document.getElementById('march_scenario_player_sop');
        const reinSopEl = document.getElementById('march_scenario_rein_sop');
        const creaturesEl = document.getElementById('march_scenario_creatures');
        if (keepEl && playerSopEl && reinSopEl && creaturesEl) {
            keepEl.checked = (msc === 'keep');
            playerSopEl.checked = (msc === 'player_sop');
            reinSopEl.checked = (msc === 'rein_sop');
            creaturesEl.checked = (msc === 'creatures');
            const activeEl = msc === 'player_sop' ? playerSopEl : (msc === 'rein_sop' ? reinSopEl : (msc === 'creatures' ? creaturesEl : keepEl));
            activeEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    return loaded;
}

/**
 * Restores the complex gear/toggle UI state flawlessly from an ultra-short numeric string.
 * Supports parsing both the final '.' separated integer array, and the legacy JSON-wrapped array fallback.
 * Emits 'change' events exactly on individual elements to functionally trigger stat recalculations.
 *
 * @param {string} encodedStr - The LZString Base64 encoded numeric payload.
 * @returns {boolean} True if successfully restored, false on structural failure.
 */
function loadCompactState(encodedStr) {
    try {
        const rawString = LZString.decompressFromEncodedURIComponent(encodedStr);
        if (!rawString) return false;

        // A. Legacy Compact JSON fallback (from the very first iteration of the array technique)
        if (rawString.startsWith('[')) {
            const payload = JSON.parse(rawString);
            const gearVals = payload[0] || [];
            const bulkVals = payload[1] || [];
            const toggleBits = payload[2] || '';
            const FIELDS = ['name', 'quality', 'level'];

            const form = document.getElementById('gearForm');
            let gearIdx = 0;
            for (const setName of SETS) {
                for (const slot of GEAR_SLOTS) {
                    for (const field of FIELDS) {
                        const val = gearVals[gearIdx++];
                        if (val) {
                            const el = form.elements[`${setName}_${slot}_${field}`];
                            if (el) { el.value = val; el.dispatchEvent(new Event('change', { bubbles: true })); }
                        }
                    }
                }
            }

            const QUICK_SELECTS = ['set1_full_set', 'set2_full_set', 'set1_pit_set', 'set2_pit_set'];
            let bulkIdx = 0;
            const bulkTypes = ['gear', 'pit'];
            const bulkFields = ['quality', 'level'];
            for (const setName of SETS) {
                for (const type of bulkTypes) {
                    for (const field of bulkFields) {
                        const val = bulkVals[bulkIdx++];
                        if (val) {
                            const el = document.getElementById(`${setName}_bulk_${type}_${field}`);
                            if (el) { el.value = val; el.dispatchEvent(new Event('change', { bubbles: true })); }
                        }
                    }
                }
            }
            for (const qs of QUICK_SELECTS) {
                const val = bulkVals[bulkIdx++];
                if (val) {
                    const el = document.getElementById(qs);
                    if (el) { el.value = val; el.dispatchEvent(new Event('change', { bubbles: true })); }
                }
            }

            for (let i = 0; i < Math.min(TOGGLES.length, toggleBits.length); i++) {
                const el = document.getElementById(TOGGLES[i]);
                if (el) {
                    el.checked = (toggleBits[i] === '1');
                    el.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
            return true;
        }

        // B. New Extreme Short Integer Format
        const parts = rawString.split('_');
        const gearVals = parts[0] ? parts[0].split('.') : [];
        const toggleInt = parseInt(parts[1] || '0', 10);
        const titleStr = parts[2] ? decodeURIComponent(parts[2]) : '';
        if (titleStr) {
            window._savedComparisonTitle = titleStr;
        }

        const form = document.getElementById('gearForm');
        let gearIdx = 0;

        for (const setName of SETS) {
            for (const slot of GEAR_SLOTS) {
                // Name
                const itemId = parseInt(gearVals[gearIdx++] || '0', 10);
                if (itemId > 0) {
                    const el = form.elements[`${setName}_${slot}_name`];
                    if (el) {
                        el.value = getItemNameById(slot, itemId);
                        el.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }

                // Quality
                const qIdx = parseInt(gearVals[gearIdx++] || '0', 10);
                if (qIdx > 0) {
                    const el = form.elements[`${setName}_${slot}_quality`];
                    if (el && QUALITY_MAP[qIdx - 1]) {
                        el.value = QUALITY_MAP[qIdx - 1];
                        el.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }

                // Level
                const lIdx = parseInt(gearVals[gearIdx++] || '0', 10);
                if (lIdx > 0) {
                    const el = form.elements[`${setName}_${slot}_level`];
                    if (el && LEVEL_MAP[lIdx - 1]) {
                        el.value = LEVEL_MAP[lIdx - 1];
                        el.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
            }
        }

        // Application of Togges from binary Int
        for (let i = 0; i < TOGGLES.length; i++) {
            const el = document.getElementById(TOGGLES[i]);
            if (el) {
                el.checked = (toggleInt & (1 << i)) !== 0;
                el.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        return true;
    } catch (e) {
        console.error('Failed to load compact state:', e);
        return false;
    }
}

function loadLegacyState(encodedState) {
    try {
        let jsonString = LZString.decompressFromEncodedURIComponent(encodedState);
        if (!jsonString) {
            jsonString = decodeURIComponent(atob(encodedState));
        }
        const state = JSON.parse(jsonString);
        const form = document.getElementById('gearForm');

        for (const [key, value] of Object.entries(state)) {
            let el = document.getElementById(key);
            if (!el && form.elements[key]) {
                el = form.elements[key];
                if (el instanceof NodeList) {
                    el = Array.from(el).find(radio => radio.value === value);
                }
            }
            if (el) {
                if (el.type === 'checkbox') {
                    el.checked = value;
                    el.dispatchEvent(new Event('change', { bubbles: true }));
                } else {
                    el.value = value;
                    el.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        }
        return true;
    } catch (e) {
        console.error('Failed to load legacy state:', e);
        return false;
    }
}
